<template>
  <div 
    class="event-item"
    :class="{ 'event-item--active': isActiveEvent }"
    :style="eventStyle"
    @mouseenter="showTooltip"
    @mouseleave="hideTooltip"
    @mousemove="updateTooltipPosition"
  >
    <div class="event-content">
    </div>
  </div>
  <div v-if="isTooltipVisible" 
       class="event-tooltip" 
       :style="{ left: tooltipPosition.x + 'px', top: tooltipPosition.y + 'px' }"
       v-html="tooltipContent">
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useBlocksStore } from '../stores/blocks'
import { formatLocalizedTime } from '../utils/time'

const props = defineProps({
  event: {
    type: Object,
    required: true
  },
  displayStart: {
    type: Date,
    required: true
  }
})

// Get store for current time updates on active events
const store = useBlocksStore()

// Tooltip state
const isTooltipVisible = ref(false)
const tooltipPosition = ref({ x: 0, y: 0 })
const tooltipTimeout = ref(null)

// Check if event is currently active (still running)
const isActiveEvent = computed(() => {
  return !props.event.ended_at || props.event.ended_at === null
})

const tooltipContent = computed(() => {
  const startTime = new Date(props.event.started_at)
  
  if (isActiveEvent.value) {
    return `<b>${props.event.timer_name}</b><br/>${formatLocalizedTime(startTime)} - Running...`
  } else {
    const endTime = new Date(props.event.ended_at)
    return `<b>${props.event.timer_name}</b><br/>${formatLocalizedTime(startTime)} - ${formatLocalizedTime(endTime)}<br/>${formatDuration(props.event.duration)}`
  }
})

const showTooltip = () => {
  // Clear any existing timeout
  if (tooltipTimeout.value) {
    clearTimeout(tooltipTimeout.value)
  }
  
  // Show tooltip after a short delay
  tooltipTimeout.value = setTimeout(() => {
    isTooltipVisible.value = true
  }, 500) // 500ms delay
}

const hideTooltip = () => {
  // Clear timeout if mouse leaves before tooltip shows
  if (tooltipTimeout.value) {
    clearTimeout(tooltipTimeout.value)
    tooltipTimeout.value = null
  }
  
  isTooltipVisible.value = false
}

const updateTooltipPosition = (e) => {
  tooltipPosition.value = {
    x: e.clientX + 10,
    y: e.clientY + 10
  }
}

const eventStyle = computed(() => {
  const startTime = new Date(props.event.started_at)
  
  // For active events, calculate duration to current time (use store's currentTime for reactivity)
  const endTime = isActiveEvent.value ? store.currentTime : new Date(props.event.ended_at)
  
  // Hide if event ends before display start
  if (props.displayStart > endTime) {
    return { display: 'none' }
  }
  
  // Calculate absolute positioning to overlay on grid items
  const minutesFromDisplayStart = (startTime - props.displayStart) / (1000 * 60)
  const durationMinutes = (endTime - startTime) / (1000 * 60)
  
  // Use same row calculation as BlockItem but convert to absolute positioning
  const startRow = Math.floor(minutesFromDisplayStart / 5) + 1
  const gridSpan = durationMinutes / 5
  
  // Convert grid rows to pixel positions (each row is 10px)
  const topPosition = (startRow - 1) * 10 + 22 // Use same offset as current time line
  const height = gridSpan * 10
  
  return {
    position: 'absolute',
    top: `${topPosition}px`,
    left: '6px', // Inset from left edge
    right: '6px', // Inset from right edge (more to avoid scrollbar)
    height: `${height}px`,
    zIndex: 2, // Between background (1) and timers (5)
  }
})

const formatDuration = (seconds) => {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  if (hours > 0) {
    return `${hours}h ${minutes}m`
  }
  return `${minutes}m`
}
</script>

<style scoped>
.event-item {
  background-color: hsla(var(--bulma-primary-h), var(--bulma-primary-s), var(--bulma-primary-l), 0.2);
  border-radius: 4px;
  box-shadow: 
    0 0 4px hsla(var(--bulma-primary-h), var(--bulma-primary-s), var(--bulma-primary-l), 0.25),
    0 0 8px hsla(var(--bulma-primary-h), var(--bulma-primary-s), var(--bulma-primary-l), 0.12);
  overflow: hidden;
  margin: 0 1px; /* Small margin to prevent scrollbar cutoff */
}

.event-item--active {
  background: linear-gradient(
    to bottom,
    hsla(var(--bulma-link-h), var(--bulma-link-s), var(--bulma-link-l), 0.4) 0%,
    hsla(var(--bulma-link-h), var(--bulma-link-s), var(--bulma-link-l), 0.15) 60%,
    hsla(var(--bulma-link-h), var(--bulma-link-s), var(--bulma-link-l), 0.05) 100%
  );
  box-shadow: 
    0 0 4px hsla(var(--bulma-link-h), var(--bulma-link-s), var(--bulma-link-l), 0.3),
    0 0 8px hsla(var(--bulma-link-h), var(--bulma-link-s), var(--bulma-link-l), 0.2);
  filter: blur(0.3px);
}

.event-content {
  padding: 2px 6px;
  height: 100%;
  display: flex;
  align-items: center;
}

.event-name {
  font-size: 0.75rem;
  color: rgba(var(--bulma-text-rgb), 0.7);
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.event-tooltip {
  position: fixed;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  pointer-events: none;
  z-index: 1000;
  white-space: pre-line;
}
</style>
